"use client";

import React from "react";
import Link from "next/link";
import { Shield, FileText, CheckCircle } from "lucide-react";

export default function KVKKPage() {
  return (
    <div className="min-h-screen bg-gray-50 pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="bg-white rounded-t-2xl shadow-sm border border-gray-100 p-8 md:p-12 text-center">
          <div className="w-16 h-16 bg-indigo-100 text-indigo-600 rounded-2xl flex items-center justify-center mx-auto mb-6">
            <Shield className="w-8 h-8" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            Kişisel Verilerin Korunması Politikası
          </h1>
          <p className="text-gray-500 max-w-2xl mx-auto">
            6698 Sayılı Kişisel Verilerin Korunması Kanunu ("KVKK") Kapsamında
            Aydınlatma Metni
          </p>
          <div className="mt-6 flex items-center justify-center gap-2 text-sm text-gray-400">
            <ClockIcon /> Son Güncelleme: 28 Ocak 2026
          </div>
        </div>

        {/* Content */}
        <div className="bg-white rounded-b-2xl shadow-sm border border-gray-100 border-t-0 p-8 md:p-12 space-y-10 text-gray-700 leading-relaxed">
          {/* Section 1 */}
          <section>
            <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4 border-b border-gray-100 pb-2">
              <span className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center text-sm">
                1
              </span>
              Veri Sorumlusu
            </h2>
            <p>
              <strong>İKAI HR Teknoloji Anonim Şirketi</strong> ("Şirket")
              olarak, kişisel verilerinizin güvenliği hususuna azami hassasiyet
              göstermekteyiz. 6698 sayılı Kişisel Verilerin Korunması Kanunu
              ("KVKK") uyarınca, kişisel verileriniz; veri sorumlusu sıfatıyla
              aşağıda açıklanan kapsamda işlenebilecektir.
            </p>
          </section>

          {/* Section 2 */}
          <section>
            <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4 border-b border-gray-100 pb-2">
              <span className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center text-sm">
                2
              </span>
              Kişisel Verilerin İşlenme Amacı
            </h2>
            <p className="mb-4">
              Toplanan kişisel verileriniz, aşağıdaki amaçlarla işlenmektedir:
            </p>
            <ul className="space-y-2 list-none">
              {[
                "Şirketimiz tarafından sunulan ürün ve hizmetlerden sizleri faydalandırmak için gerekli çalışmaların iş birimlerimiz tarafından yapılması,",
                "Şirket insan kaynakları politikalarının ve süreçlerinin planlanması ve icra edilmesi,",
                "Şirket tarafından yürütülen ticari faaliyetlerin gerçekleştirilmesi için ilgili iş birimlerimiz tarafından gerekli çalışmaların yapılması ve buna bağlı iş süreçlerinin yürütülmesi,",
                "Şirketimizin ve Şirketimizle iş ilişkisi içerisinde olan ilgili kişilerin hukuki, teknik ve ticari-iş güvenliğinin temini.",
              ].map((item, i) => (
                <li key={i} className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </section>

          {/* Section 3 */}
          <section>
            <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4 border-b border-gray-100 pb-2">
              <span className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center text-sm">
                3
              </span>
              Kişisel Verilerin Toplanma Yöntemi ve Hukuki Sebebi
            </h2>
            <p>
              Kişisel verileriniz, Şirketimiz tarafından web sitemiz, mobil
              uygulamamız, çağrı merkezimiz ve fiziki kanallar aracılığıyla
              elektronik veya fiziki ortamda toplanmaktadır. Bu süreçte toplanan
              kişisel verileriniz; KVKK'nın 5. ve 6. maddelerinde belirtilen
              kişisel veri işleme şartları ve amaçları kapsamında, hem
              kanunlarda açıkça öngörülmesi hem de sözleşmenin ifası hukuki
              sebeplerine dayanılarak işlenmektedir.
            </p>
          </section>

          {/* Section 4 */}
          <section>
            <h2 className="flex items-center gap-3 text-xl font-bold text-gray-900 mb-4 border-b border-gray-100 pb-2">
              <span className="w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center text-sm">
                4
              </span>
              Kişisel Veri Sahibinin Hakları
            </h2>
            <p className="mb-4">
              KVKK'nın 11. maddesi uyarınca veri sahipleri olarak aşağıdaki
              haklara sahipsiniz:
            </p>
            <div className="grid md:grid-cols-2 gap-4">
              {[
                "Kişisel veri işlenip işlenmediğini öğrenme,",
                "Kişisel verileri işlenmişse buna ilişkin bilgi talep etme,",
                "Kişisel verilerin işlenme amacını ve amacına uygun kullanılıp kullanılmadığını öğrenme,",
                "Yurt içinde veya yurt dışında kişisel verilerin aktarıldığı üçüncü kişileri bilme,",
                "Kişisel verilerin eksik veya yanlış işlenmiş olması hâlinde bunların düzeltilmesini isteme,",
                "KVKK'nın 7. maddesinde öngörülen şartlar çerçevesinde kişisel verilerin silinmesini veya yok edilmesini isteme.",
              ].map((right, i) => (
                <div
                  key={i}
                  className="bg-gray-50 p-4 rounded-lg text-sm border border-gray-100 flex items-start gap-3"
                >
                  <div className="w-2 h-2 bg-indigo-400 rounded-full mt-2"></div>
                  {right}
                </div>
              ))}
            </div>
          </section>

          {/* Contact Box */}
          <div className="bg-gradient-to-r from-indigo-50 to-purple-50 rounded-xl p-8 border border-indigo-100 mt-8">
            <h3 className="text-lg font-bold text-indigo-900 mb-2">
              Bize Ulaşın
            </h3>
            <p className="text-indigo-800 mb-4">
              KVKK kapsamındaki haklarınızı kullanmakla ilgili taleplerinizi,
              yazılı olarak veya Kişisel Verileri Koruma Kurulu'nun belirlediği
              diğer yöntemlerle bize iletebilirsiniz.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                href="/iletisim"
                className="inline-flex items-center justify-center px-6 py-3 bg-white text-indigo-600 font-semibold rounded-lg border border-indigo-200 hover:shadow-md transition-all"
              >
                İletişim Formu
              </Link>
              <a
                href="mailto:kvkk@gaiai.ai"
                className="inline-flex items-center justify-center px-6 py-3 bg-indigo-600 text-white font-semibold rounded-lg hover:bg-indigo-700 hover:shadow-lg transition-all"
              >
                <FileText className="w-4 h-4 mr-2" />
                kvkk@gaiai.ai
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function ClockIcon() {
  return (
    <svg
      className="w-4 h-4"
      fill="none"
      viewBox="0 0 24 24"
      stroke="currentColor"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
      />
    </svg>
  );
}
